import type { Schema } from 'hast-util-sanitize';
import { defaultSchema } from 'rehype-sanitize';

/**
 * Spec 031: raw HTML in a policy is kept only when it is a link or a span id.
 * `javascript:` and event handlers are not in this schema, so they are removed.
 * rehype-sanitize runs after rehype-raw.
 */
const baseSchema = defaultSchema ?? {};

export const policySanitizeSchema: Schema = {
  ...baseSchema,
  attributes: {
    ...(baseSchema.attributes ?? {}),
    a: [...(baseSchema.attributes?.a ?? []), 'id', 'name', 'href', 'target', 'rel'],
    span: ['id', 'className'],
  },
  protocols: {
    ...(baseSchema.protocols ?? {}),
    href: ['http', 'https', 'mailto', 'tel'],
  },
};

/** `target="_blank"` links get `rel="noopener noreferrer"`. */
export function rehypeBlankLinkRel() {
  return (tree: { children?: unknown[] }) => {
    const walk = (node: any) => {
      if (!node || typeof node !== 'object') return;
      if (node.type === 'element' && node.tagName === 'a') {
        const target = node.properties?.target;
        const blank = target === '_blank' || (Array.isArray(target) && target.includes('_blank'));
        if (blank) {
          node.properties = { ...node.properties, rel: ['noopener', 'noreferrer'] };
        }
      }
      for (const child of node.children ?? []) walk(child);
    };
    walk(tree);
  };
}

/** "Last updated October 8, 2026 · Version 2" and, for git imports, "dates approximate". */
export function policyVersionLine(input: {
  effectiveDate?: string | null;
  versionNumber?: number | null;
  approximateDates?: boolean;
}): string | null {
  if (!input.effectiveDate && (input.versionNumber == null || input.versionNumber === undefined)) {
    return null;
  }
  const date = input.effectiveDate ? formatPolicyDate(input.effectiveDate) : '';
  const version =
    input.versionNumber == null || input.versionNumber === undefined
      ? ''
      : `Version ${input.versionNumber}`;
  const parts = [date ? `Last updated ${date}` : '', version].filter(Boolean);
  if (input.approximateDates) parts.push('dates approximate');
  return parts.join(' · ');
}

function formatPolicyDate(iso: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) return iso;
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}
